/**
 * schema.ts — DemoState type definitions.
 *
 * POLICY: archivedAt is the single source of truth for archive lifecycle.
 * status represents the functional state of an issue/draft.
 * statusBeforeArchive preserves the functional state so restoreIssue can recover it.
 *
 * Legacy data with status === 'arxivat' but archivedAt === null/undefined
 * is treated as archived and normalised by migrateDemoState().
 *
 * Forbidden combination after migration:
 *   status: 'arxivat' && archivedAt: null
 */

import type { PlenarySession, AgendaItem, Document, Draft, Issue, IssueStatus, SuggestedQuestion, ActivityEvent,  } from '@/types/models';

export const DEMO_STATE_KEY = 'plenaricat_demo_state_v1';
export const DEMO_STATE_BACKUP_PREFIX = 'plenaricat_demo_state_backup_';
export const CURRENT_VERSION = 1;
export const CURRENT_SCHEMA_VERSION = 3;

// ─── Multi-role user models ────────────────────────────────────────────

export interface UserProfile {
  id: string;
  nom: string;
  cognoms?: string;
  email: string;
  telefon?: string | null;
  avatarInicials?: string;
}

export type OrganizationRole =
  | 'administracio' |'coordinacio_territorial' |'presidencia_local' |'secretaria_local' |'tresoreria_local' |'vocal_1' |'vocal_2' |'vocal' |'membre_comite_local';

export interface OrganizationMembership {
  id: string;
  userId: string;
  organizationId: string;
  activa: boolean;
  roles: OrganizationRole[];
  customRoleLabel?: string | null;
}

export type InstitutionalRole =
  | 'regidor'
  | 'portaveu' |'alcalde' |'assessor' |'personal_tecnic' |'sense_carrec_institucional';

export interface WorkspaceMembership {
  id: string;
  userId: string;
  workspaceId: string;
  institutionalRoles: InstitutionalRole[];
  active: boolean;
}

// ─── Workspace model ───────────────────────────────────────────────────

export interface DemoWorkspace {
  id: string;
  municipalityId: string;
  organizationId: string;
  nom: string;
  rolUsuari: string;
  actiu: boolean;
  creatA: string;
}

// ─── OrganizationMunicipalityConfig (persistent overrides) ────────────

export interface DemoOrgMunicipalityConfig {
  id: string;
  organizationId: string;
  municipalityId: string;
  organizationEnabled: boolean;
  workspaceStatus: string;
  politicalStatus: string;
  workspaceId?: string | null;
  estructuraLocal: boolean;
  dadesDemoDisponibles: boolean;
  visibleAlDirectoriIntern: boolean;
  dataActualitzacio?: string | null;
  fontInterna?: string | null;
  notesInternes?: string | null;
  /** Soft-delete: ISO timestamp when moved to trash. null = active. */
  trashedAt?: string | null;
  /** Soft-delete: user identifier who trashed this config. */
  trashedBy?: string | null;
}

// ─── Notification model ────────────────────────────────────────────────

export interface DemoNotification {
  id: string;
  municipalityId: string;
  workspaceId?: string | null;
  entityId?: string | null;
  entityType?: 'plenary' | 'agendaItem' | 'document' | 'issue' | 'draft' | null;
  titol: string;
  descripcio: string;
  data: string;
  llegit: boolean;
  tipus: string;
  prioritat: 'alta' | 'mitjana' | 'baixa';
}

// ─── Issue with statusBeforeArchive ───────────────────────────────────

export interface DemoIssue extends Issue {
  /**
   * Preserves the functional status before archiving so restoreIssue
   * can recover it. Never set manually — managed by archiveIssue/restoreIssue.
   */
  statusBeforeArchive?: IssueStatus | null;
}

// ─── Preferences ──────────────────────────────────────────────────────

export interface DemoPreferences {
  theme: 'clar' | 'fosc' | 'sistema';
  notifNouDocument: boolean;
  notifTerminis: boolean;
  notifEsborranys: boolean;
  notifAssumptes: boolean;
}

// ─── Metadata ─────────────────────────────────────────────────────────

export interface DemoMetadata {
  createdAt: string;
  lastModifiedAt: string;
  seedVersion: string;
}

// ─── Root DemoState ───────────────────────────────────────────────────

export interface DemoState {
  version: number;
  schemaVersion: number;

  // User
  userProfiles: UserProfile[];
  organizationMemberships: OrganizationMembership[];
  workspaceMemberships: WorkspaceMembership[];

  // Workspaces
  workspaces: DemoWorkspace[];
  activeWorkspaceId: string | null;
  activeOrganizationId: string | null;

  // Organization configs (persistent overrides)
  organizationMunicipalityConfigs: DemoOrgMunicipalityConfig[];

  // Plenary data
  plenarySessions: PlenarySession[];
  agendaItems: AgendaItem[];
  documents: Document[];
  citations: unknown[];

  // Issues & Drafts
  issues: DemoIssue[];
  drafts: Draft[];
  suggestedQuestions: SuggestedQuestion[];

  // Notes (map: agendaItemId → string[])
  internalNotes: Record<string, string[]>;
  itemResponsables: Record<string, string>;

  // Activity & Alerts
  activityEvents: ActivityEvent[];
  alerts: DemoNotification[];

  // Notifications (read state)
  notifications: DemoNotification[];

  // Preferences
  preferences: DemoPreferences;

  // Metadata
  metadata: DemoMetadata;
}
